// assets/hbk-subscribe-popup.js
(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────
  const SELECTORS = {
    root:    '[data-component="subscribe-popup"]',
    dialog:  '.hbk-sub__dialog',
    bubble:  '[data-sub-bubble]',
    form:    '.hbk-sub__form',
    error:   '.hbk-sub__error',
    close:   '[data-sub-close]',
    later:   '[data-sub-later]',
    submit:  '[type="submit"]',
    email:   '[type="email"]',
    focusable: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])',
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const POLL_MS = 400;          // how often we re-check for blocking overlays

  // ── State ──────────────────────────────────────────────────────
  const instances = new Map();

  // ── Storage helpers ────────────────────────────────────────────
  // One record per popup: { status: 'minimized' | 'dismissed' | 'subscribed', ts: <epoch ms> }
  function readState(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(key, status) {
    try {
      localStorage.setItem(key, JSON.stringify({ status, ts: Date.now() }));
    } catch (e) { /* private mode — the popup simply reappears next visit */ }
  }

  function seenThisSession(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }

  // ── Debug trail ────────────────────────────────────────────────
  // Kept in sessionStorage so it SURVIVES the page navigation a native submit causes. Without
  // this, every clue is wiped by the reload before it can be read.
  function logPush(key, msg) {
    try {
      const k = key + ':log';
      const arr = JSON.parse(sessionStorage.getItem(k) || '[]');
      arr.push(new Date().toISOString().slice(11, 19) + ' ' + msg);
      sessionStorage.setItem(k, JSON.stringify(arr.slice(-12)));
    } catch (e) {}
  }

  function logRead(key) {
    try { return JSON.parse(sessionStorage.getItem(key + ':log') || '[]'); } catch (e) { return []; }
  }

  function markSession(key) {
    try { sessionStorage.setItem(key, '1'); } catch (e) {}
  }

  // ── Core class ─────────────────────────────────────────────────
  class SubscribePopup {
    constructor(el) {
      this.el      = el;
      this.root    = el.closest('.shopify-section') || el.parentElement || document;
      this.dialog  = el.querySelector(SELECTORS.dialog);
      this.bubble  = this.root.querySelector ? this.root.querySelector(SELECTORS.bubble) : null;
      this.form    = el.querySelector(SELECTORS.form);
      this.errorEl = el.querySelector(SELECTORS.error);

      this.design       = el.dataset.designMode === 'true';
      this.editorView   = el.dataset.editorPreview || 'dialog';
      this.delay        = (parseFloat(el.dataset.delay) || 0) * 1000;
      this.waitBlocking = el.dataset.waitForBlocking === 'true';
      this.selectors    = (el.dataset.blockingSelectors || '').trim();
      this.maxWait      = (parseFloat(el.dataset.maxWait) || 45) * 1000;
      this.frequency    = el.dataset.frequency || 'days';
      this.reshowDays   = parseFloat(el.dataset.reshowDays) || 14;
      this.closeAction  = el.dataset.closeAction || 'minimize';
      this.storageKey   = el.dataset.storageKey || 'hbk-subscribe';
      this.errorText    = el.dataset.errorText || 'Something went wrong. Please try again.';
      this.invalidText  = el.dataset.invalidText || 'Please enter a valid email address.';
      this.debug        = el.dataset.debug === 'true';

      this.prevFocus  = null;
      this.timer      = null;
      this.poll       = null;
      this.successT   = null;
      this._onKey     = this._onKey.bind(this);

      this._bind();
      if (this.debug) {
        logPush(this.storageKey, 'init js=ok form=' + (this.form ? 'found' : 'NULL') +
                ' url=' + location.pathname + location.search);
      }
      this._start();
    }

    // ── Wiring ───────────────────────────────────────────────────
    _bind() {
      // ✕ button and overlay honour the merchant's "when closed" choice.
      this.el.querySelectorAll(SELECTORS.close).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeAction === 'dismiss' ? this.dismiss() : this.minimize();
        });
      });

      // "Vielleicht später" always shrinks to the bubble — that is its whole point.
      this.el.querySelectorAll(SELECTORS.later).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.minimize();
        });
      });

      if (this.bubble) {
        this.bubble.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }

      // Delegated on the document, in the CAPTURE phase, rather than bound to the form node.
      // Evidence from the storefront: on submit the page NAVIGATED, i.e. no handler ran at all,
      // even though the form is present and correctly nested. A listener bound directly to the
      // form node is lost if anything replaces or re-renders that subtree after we bind — GDPR
      // consent and performance apps do exactly that. Delegation survives it, and capture phase
      // means we run before any other submit handler on the page.
      this._onDocSubmit = (e) => {
        const form = e.target && e.target.closest ? e.target.closest('.hbk-sub__form') : null;
        if (this.debug) logPush(this.storageKey, 'submit seen, ours=' + !!(form && this.el.contains(form)));
        if (!form || !this.el.contains(form)) return;   // not this popup's form
        this.form = form;                               // re-point: the node may have been swapped
        this._markPending();
        e.preventDefault();
        if (this.debug) logPush(this.storageKey, 'INTERCEPTED, preventDefault called');
        this._submit();
      };
      document.addEventListener('submit', this._onDocSubmit, true);
    }

    // ── Decide whether / when to appear ──────────────────────────
    _start() {
      // Theme Editor: never persist anything, just render what the merchant asked for.
      if (this.design) {
        if (this.editorView === 'dialog') this.open();
        else if (this.editorView === 'bubble') this._showBubble();
        return;
      }

      // Shopify appends customer_posted=true to the page it returns to after a signup — a far
      // more reliable signal than our breadcrumb, which the captcha detour can eat.
      //
      // We deliberately do NOT require Shopify's [data-sub-posted] marker here. The captcha
      // round-trip (/challenge and back) frequently drops it, and demanding it is what left
      // mobile visitors with no confirmation and a popup that kept returning after they had
      // already subscribed. The breadcrumb proves this visitor submitted this popup, which is
      // enough: treat it as done rather than nag someone who just signed up. Worst case is a
      // visitor who abandoned the challenge stops being asked — far better than the reverse.
      const hadPending = this._consumePending();
      const posted = /[?&]customer_posted=true/.test(window.location.search);
      if (hadPending || posted) {
        if (this.debug) logPush(this.storageKey, 'success path: pending=' + hadPending + ' posted=' + posted);
        this._success();   // also writes the 'subscribed' flag, so we stop reappearing
        this.open();
        return;
      }

      const state = readState(this.storageKey);

      if (state?.status === 'subscribed') return;              // already on the list
      if (state?.status === 'minimized') { this._showBubble(); return; }

      if (state?.status === 'dismissed') {
        if (this.frequency === 'never') return;
        if (this.frequency === 'days') {
          const elapsed = Date.now() - (state.ts || 0);
          if (elapsed < this.reshowDays * 86400000) return;
        }
      }

      if (this.frequency === 'session' && seenThisSession(this.storageKey)) return;

      this._schedule();
    }

    // Wait for consent / region overlays to clear, then run the merchant's delay.
    _schedule() {
      const startedAt = Date.now();

      const fire = () => {
        this.timer = setTimeout(() => this.open(), this.delay);
      };

      if (!this.waitBlocking || !this.selectors) { fire(); return; }

      const clear = () => {
        if (Date.now() - startedAt >= this.maxWait) return true;  // safety cap reached
        return !this._blockingVisible();
      };

      if (clear()) { fire(); return; }

      this.poll = setInterval(() => {
        if (!clear()) return;
        clearInterval(this.poll);
        this.poll = null;
        fire();
      }, POLL_MS);
    }

    // An overlay counts as blocking only while it is actually rendered.
    _blockingVisible() {
      let nodes;
      try {
        nodes = document.querySelectorAll(this.selectors);
      } catch (e) {
        return false;  // a malformed selector must never stop the popup
      }
      return Array.from(nodes).some((n) => {
        if (n === this.el || this.el.contains(n) || n.contains(this.el)) return false;
        if (n.hasAttribute('hidden') || n.getAttribute('aria-hidden') === 'true') return false;
        const cs = window.getComputedStyle(n);
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
        return n.getBoundingClientRect().height > 0;
      });
    }

    // ── Open / close ─────────────────────────────────────────────
    open() {
      if (this.el.classList.contains('is-open')) return;
      this._clearTimers();
      this._hideBubble();

      this.prevFocus = document.activeElement;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      // Don't trap the page in the Theme Editor — the merchant still needs to scroll.
      if (!this.design) document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);

      if (!this.design) markSession(this.storageKey);

      if (this.debug) {
        const trail = logRead(this.storageKey);
        this._error('DEBUG form=' + (this.form ? 'found' : 'NULL') +
                    ' delegated=' + (this._onDocSubmit ? 'yes' : 'no') +
                    ' | TRAIL: ' + (trail.length ? trail.join(' || ') : '(empty)'));
      }

      const f = this._focusable();
      if (f.length) setTimeout(() => f[0].focus(), 60);
    }

    close() {
      if (!this.el.classList.contains('is-open')) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
      if (this.prevFocus && this.prevFocus.focus) this.prevFocus.focus();
    }

    // Shrink to the corner bubble — the visitor can pull it back up any time.
    minimize() {
      this.close();
      this._showBubble();
      if (!this.design) writeState(this.storageKey, 'minimized');
    }

    // Gone for good (until the frequency window reopens).
    dismiss() {
      this.close();
      this._hideBubble();
      if (!this.design) writeState(this.storageKey, 'dismissed');
    }

    _showBubble() {
      if (!this.bubble) return;
      this.bubble.removeAttribute('hidden');
      // Force a frame so the entrance transition runs from its start state.
      requestAnimationFrame(() => this.bubble.classList.add('is-visible'));
    }

    _hideBubble() {
      if (!this.bubble) return;
      this.bubble.classList.remove('is-visible');
      this.bubble.setAttribute('hidden', '');
    }

    // ── Signup ───────────────────────────────────────────────────
    _markPending() {
      try { sessionStorage.setItem(this.storageKey + ':pending', '1'); } catch (e) {}
    }

    _clearPending() {
      try { sessionStorage.removeItem(this.storageKey + ':pending'); } catch (e) {}
    }

    // True exactly once, on the page load that follows a submit from THIS popup. Other customer
    // forms on the page also report posted_successfully, so the flag stops us claiming theirs.
    _consumePending() {
      try {
        if (sessionStorage.getItem(this.storageKey + ':pending') !== '1') return false;
        this._clearPending();
        return true;
      } catch (e) { return false; }
    }

    async _submit() {
      const emailEl = this.form.querySelector(SELECTORS.email);
      const submit  = this.form.querySelector(SELECTORS.submit);
      const email   = emailEl?.value?.trim();

      if (!email || !EMAIL_RE.test(email)) {
        this._error(this.invalidText);
        emailEl?.focus();
        return;
      }

      submit?.setAttribute('aria-busy', 'true');
      if (submit) submit.disabled = true;
      this._clearError();

      try {
        const body = new URLSearchParams(new FormData(this.form)).toString();
        const res = await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'same-origin',
          body,
        });
        if (this.debug) {
          // Diagnostic mode: report what the server actually said, from the visitor's own device.
          const text = await res.text().catch(() => '(body unreadable)');
          this._clearPending();
          logPush(this.storageKey, 'fetch done status=' + res.status + ' ok=' + res.ok);
          this._error(
            'DEBUG status=' + res.status + ' ok=' + res.ok +
            ' redirected=' + res.redirected + ' type=' + res.type +
            ' url=' + (res.url || '-') +
            ' body=' + text.replace(/\s+/g, ' ').slice(0, 160)
          );
          return;
        }

        // The request completed. Spam protection is OFF on this store, so there is no challenge
        // to detect, and the response status has proven unreliable as a success signal (signups
        // land while the status is not "ok"). A completed request counts as success.
        this._clearPending();
        this._success();
      } catch (err) {
        // Only a genuine network failure reaches here.
        this._clearPending();
        this._error(this.debug ? ('DEBUG network error: ' + (err && err.message)) : this.errorText);
      } finally {
        submit?.setAttribute('aria-busy', 'false');
        if (submit) submit.disabled = false;
      }
    }

    _success() {
      this.dialog?.classList.remove('has-error');
      this.dialog?.classList.add('is-success');
      if (!this.design && !this.debug) writeState(this.storageKey, 'subscribed');
      this._hideBubble();
      // Deliberately NOT auto-closing: the visitor must actually see the confirmation.
      // They close it themselves via ✕ / overlay / Escape.
    }

    _error(msg) {
      this.dialog?.classList.add('has-error');
      if (this.errorEl) this.errorEl.textContent = msg;
    }

    _clearError() {
      this.dialog?.classList.remove('has-error');
      if (this.errorEl) this.errorEl.textContent = '';
    }

    // ── Accessibility ────────────────────────────────────────────
    _focusable() {
      return Array.from(this.dialog.querySelectorAll(SELECTORS.focusable))
        .filter((n) => n.offsetParent !== null || n === document.activeElement);
    }

    _onKey(e) {
      if (e.key === 'Escape') {
        this.closeAction === 'dismiss' ? this.dismiss() : this.minimize();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = this._focusable();
      if (!f.length) return;
      const first = f[0];
      const last  = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    _clearTimers() {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
      if (this.poll) { clearInterval(this.poll); this.poll = null; }
    }

    destroy() {
      this._clearTimers();
      if (this.successT) { clearTimeout(this.successT); this.successT = null; }
      document.removeEventListener('keydown', this._onKey);
      if (this._onDocSubmit) document.removeEventListener('submit', this._onDocSubmit, true);
      document.body.style.overflow = '';
    }
  }

  // ── Init & Theme Editor safety ─────────────────────────────────
  // Shopify's captcha script navigates to /challenge on its own, even when we call
  // preventDefault — and that page renders this theme, so the popup boots there too. Proven by
  // the storefront trail: the popup initialised on /challenge and consumed the breadcrumb that
  // the real return page needed, which is why the confirmation never appeared. It must also
  // never cover the captcha the visitor has to solve. So: stay out entirely.
  function onChallengePage() {
    return /^\/challenge(\/|$|\?)/.test(window.location.pathname + window.location.search) ||
           window.location.pathname.indexOf('/challenge') === 0;
  }

  function init(scope) {
    const skip = onChallengePage();
    (scope || document).querySelectorAll(SELECTORS.root).forEach((el) => {
      if (skip) {
        if (el.dataset.debug === 'true') {
          logPush(el.dataset.storageKey || 'hbk-subscribe', 'on /challenge — popup skipped');
        }
        return;
      }
      if (instances.has(el)) return;
      instances.set(el, new SubscribePopup(el));
    });
  }

  function destroy(scope) {
    (scope || document).querySelectorAll(SELECTORS.root).forEach((el) => {
      const inst = instances.get(el);
      if (!inst) return;
      inst.destroy();
      instances.delete(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load',   (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    const el = e.target.querySelector(SELECTORS.root);
    const inst = el && instances.get(el);
    if (inst && inst.design && inst.editorView !== 'hidden') {
      inst.editorView === 'bubble' ? inst._showBubble() : inst.open();
    }
  });
})();
